import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalV2 from 'common/components/Modal/ModalV2';
import CopyButton from 'common/components/CopyButton';
import { Button } from 'common/components/Form/Button';
import Icon from 'common/components/Icon';
import DialogFocusTrap from 'modules/settings/components/DataExport/DialogFocusTrap';
import { Text } from 'common/styledComponents/typography';
import { CheckboxLabel, ModalActions, ModalBody, SecretField, SecretRow, Warning } from 'modules/settings/components/DataExport/styled';

interface Props {
  modalId: string;
  title: string;
  /** The token or secret. It lives only in this modal's props and is gone once the modal closes. */
  value: string;
  /** Optional line above the warning, e.g. which token this is. */
  details?: string;
  warning: string;
  acknowledgeLabel: string;
  onClose: () => void;
}

/**
 * Shows a credential the backend returns exactly once. There is no close button and no click-outside
 * close: the user has to confirm they stored it before "Done" is enabled. Keyboard focus cannot leave the
 * dialog and the page behind it is inert, so it cannot be left (losing the credential) by tabbing to a link.
 */
const OnceOnlySecretModal: React.FC<Props> = ({ modalId, title, value, details, warning, acknowledgeLabel, onClose }) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = React.useState(false);

  return (
    <ModalV2
      isOpen
      appearance='card'
      modalId={modalId}
      title={title}
      clickOutsideClose={false}
      onClose={onClose}
      customWidth={600}
      data-testid='once-only-secret-modal'
    >
      <DialogFocusTrap label={title}>
        <ModalBody>
          {details && (
            <Text variant='body2' color='black' fontWeight='600'>
              {details}
            </Text>
          )}
          <Warning role='alert'>
            <Icon icon='alert-fill' size={20} color='orange700' />
            <Text variant='body2' color='black'>
              {warning}
            </Text>
          </Warning>
          <SecretRow>
            <SecretField
              readOnly
              value={value}
              aria-label={title}
              onFocus={e => e.currentTarget.select()}
              data-testid='once-only-secret-value'
            />
            <CopyButton value={value} />
          </SecretRow>
          <CheckboxLabel>
            <input type='checkbox' checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} />
            {acknowledgeLabel}
          </CheckboxLabel>
          <ModalActions>
            <Button type='button' color='lightPurple' pill={false} isButtonV2 disabled={!acknowledged} onClick={onClose}>
              {t('dataExport.done')}
            </Button>
          </ModalActions>
        </ModalBody>
      </DialogFocusTrap>
    </ModalV2>
  );
};

export default OnceOnlySecretModal;
